import { formatCurrencyWithLocalString } from "src/helpers/numberFormatter";
import moment from "moment-timezone";
import { SectorProps } from "src/components/patternInsight/SectorPanel";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";

export const escapeLike = (str: string) => {
  return str.replace(/[\\%_]/g, "\\$&");
};

export const getLocalStorageUser = () => {
  if (typeof window === "undefined") return false;
  return (
    localStorage.getItem("BetaUser") !== null &&
    JSON.parse(localStorage.getItem("BetaUser") || "")
  );
};

export function formatNumberWithSuffix(
  value: number | string | null | undefined,
) {
  const num = Number(value || 0);
  const absNumber = Math.abs(num);

  if (absNumber >= 1000000) {
    // Format as millions
    const formatted = (num / 1000000).toFixed(1); // One decimal place
    return `${formatted}M`;
  } else if (absNumber >= 1000) {
    // Format as thousands
    const formatted = (num / 1000).toFixed(1); // One decimal place
    return `${formatted}K`;
  } else {
    // No suffix needed for smaller numbers
    return num.toString();
  }
}

export function parseMarketCap(value: string | number | null): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;

  const cleaned = value.replace(/,/g, "").replace(/[^0-9.]/g, "");
  const num = Number(cleaned);
  if (value.toLowerCase().includes("cr")) {
    return num * 1e7;
  }
  if (value.toLowerCase().includes("lakh")) {
    return num * 1e5;
  }
  if (value.toLowerCase().includes("k")) {
    return num * 1e3;
  }

  return num;
}

export function formatToCr(value: null | number) {
  if (value === null || value === undefined) return "-";

  const num = Number(value);
  if (isNaN(num)) return "";
  const croreValue = num / 1e7;
  const roundedValue = Math.round(croreValue);

  return roundedValue.toLocaleString("en-IN") + " Cr";
}

export function capitalizeFullName(name: string): string {
  if (!name) return "";
  return name
    .trim()
    .split(" ")
    .filter((part) => part.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function getMonthName(date: Date | string): string {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const d = new Date(date);
  const monthName = months[d.getMonth()];
  const year = d.getFullYear().toString().slice(-2); // last two digits of year

  return `${monthName}'${year}`;
}

export const getInitials = (text?: string) => {
  if (!text) return "";

  const words = text.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  const firstInitial = words[0].charAt(0);
  const lastInitial = words[words.length - 1].charAt(0);

  return (firstInitial + lastInitial).toUpperCase();
};

export const formatNumber = (
  value: number | string | null | undefined,
  withoutSymbol?: boolean,
): string => {
  const num = Number(value || 0);
  let formattedValue = num.toFixed(2).replace(/\.00$/, "");
  let postFix = "";
  if (num >= 1e7) {
    formattedValue = (num / 1e7).toFixed(2).replace(/\.00$/, "");
    postFix = " Cr";
  } else if (num >= 1e5) {
    formattedValue = (num / 1e5).toFixed(2).replace(/\.00$/, "");
    postFix = " L";
  } else if (num >= 1e3) {
    formattedValue = (num / 1e3).toFixed(2).replace(/\.00$/, "");
    postFix = " K";
  }
  if (withoutSymbol) return formattedValue + postFix;
  return formatCurrencyWithLocalString(formattedValue) + postFix;
};

export const getDateMonthsAgo = (months: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  d.setHours(0, 0, 0, 0);
  return d;
};

export type DashboardDateValidationResult =
  | { success: true; date: Date }
  | { success: false; message: string };

export const validateDashboardDateParam = (
  dateParam: string | undefined,
  _nextWorkingDay?: string,
): DashboardDateValidationResult => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  // const dateCompare = nextWorkingDay
  //   ? nextWorkingDay
  //   : moment.tz(new Date(), "Asia/Kolkata").format("YYYY-MM-DD");
  if (
    !dateParam ||
    typeof dateParam !== "string" ||
    !dateRegex.test(dateParam)
  ) {
    return {
      success: false,
      message: "Invalid date format. Expected format: YYYY-MM-DD",
    };
  }
  // if (
  //   moment
  //     .tz(dateParam, "Asia/Kolkata")
  //     .isAfter(moment.tz(dateCompare, "Asia/Kolkata"))
  // ) {
  //   return {
  //     success: false,
  //     message: "Date cannot be in the future",
  //   };
  // }
  const validatedDate = new Date(dateParam);
  if (isNaN(validatedDate.getTime())) {
    return {
      success: false,
      message: "Invalid date",
    };
  }
  return { success: true, date: validatedDate };
};

export function formatDate(dateStr: string): string {
  const dateIST = moment.utc(dateStr).tz("Asia/Kolkata");

  const day = dateIST.format("DD");
  const month = dateIST.format("MMM");
  const year = dateIST.format("YY");

  return `${day} ${month} '${year}`;
}
export const formatNewsDate = (dateString: string) => {
  if (!dateString) return "";
  const isoDate = dateString.includes("T")
    ? dateString
    : dateString.replace(" ", "T");
  return (
    moment.tz(isoDate, "Asia/Kolkata").format("MMM D, YYYY, h:mm A") + " (IST)"
  );
};

export function formatWithOrdinal(dateStr: Date): string {
  const dateIST = moment.utc(dateStr).tz("Asia/Kolkata");

  const day = +dateIST.format("DD");
  const month = dateIST.format("MMM");
  const year = dateIST.format("YY");
  // Get ordinal suffix for day
  const getOrdinalSuffix = (n: number) => {
    if (n >= 11 && n <= 13) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  const suffix = getOrdinalSuffix(day);
  return `${day}<sup>${suffix}</sup> ${month} ${year}`;
}

export const divideSectors = (sectors: SectorProps[]) => {
  const total = sectors.length;
  if (total === 0) return [[], [], []];

  const base = Math.floor(total / 3);
  const remainder = total % 3;

  const counts = [
    base + (remainder > 0 ? 1 : 0),
    base + (remainder > 1 ? 1 : 0),
    base,
  ];

  const top = sectors.slice(0, counts[0]);
  const mid = sectors.slice(counts[0], counts[0] + counts[1]);
  const bottom = sectors.slice(counts[0] + counts[1]);

  return [top, mid, bottom];
};

export const isPlanExpiringSoon = (expiryDateString: string) => {
  if (!expiryDateString) return false;

  // expiryDateString received as DD/MM/YYYY
  const [day, month, year] = expiryDateString.split("/");
  const expiryDate = new Date(`${year}-${month}-${day}`);

  const today = new Date();
  const diffInTime = expiryDate.getTime() - today.getTime();

  // Convert difference to days
  const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));

  return diffInDays <= 7; // true if <= 7 days left
};

export const getSearchStatus = () => {
  if (typeof window === "undefined") return { date: "", searchedSymbols: [] };
  const today = new Date().toISOString().split("T")[0];
  const saved = JSON.parse(localStorage.getItem("searchStatus") || "{}");

  // first time ever
  if (!saved.date) {
    const initState = {
      date: today,
      searchedSymbols: [],
    };
    localStorage.setItem("searchStatus", JSON.stringify(initState));
    return initState;
  }

  // NEW DAY → reset ONLY when user searches
  if (saved.date !== today) {
    return {
      date: saved.date,
      searchedSymbols: saved.searchedSymbols,
    };
  }

  return saved;
};

export const updateSearchStatus = (
  updates: Partial<{
    searchCount: number;
    date: string;
    searchedSymbols: string[];
  }>,
) => {
  if (typeof window === "undefined") return;
  const today = new Date().toISOString().split("T")[0];

  const current = getSearchStatus();

  const newState = {
    ...current,
    ...updates,
    date: today,
  };

  localStorage.setItem("searchStatus", JSON.stringify(newState));
  return newState;
};

export const SENTIMENT_STYLES: Record<string, { bg: string; text: string }> = {
  Positive: {
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-600 dark:text-green-400",
  },
  Negative: {
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-600 dark:text-red-400",
  },
  Neutral: {
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-500 dark:text-yellow-500",
  },
  None: {
    bg: "bg-gray-100 dark:bg-gray-900/30",
    text: "text-gray-600 dark:text-white",
  },
};

export const toTitleCase = (str: string) =>
  str
    ?.toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
export const getDisplayLabel = (item: string) =>
  item === "-" ? "Not available" : item;

export const getLastTwelveMonthIndices = (dates: string[]) => {
  const monthChangeIndices: number[] = [];
  dates.forEach((date, index) => {
    const current = new Date(date);
    if (index === 0) {
      monthChangeIndices.push(index);
    } else {
      const prev = new Date(dates[index - 1]);
      if (current.getMonth() !== prev.getMonth()) {
        monthChangeIndices.push(index);
      }
    }
  });

  return monthChangeIndices.slice(-12);
};

export const getAllMonthChangeIndices = (dates: string[]): number[] => {
  const monthChangeIndices: number[] = [];
  let lastMonthKey = "";
  dates.forEach((date, index) => {
    const monthKey = moment(date).format("YYYY-MM");
    if (monthKey !== lastMonthKey) {
      monthChangeIndices.push(index);
      lastMonthKey = monthKey;
    }
  });

  return monthChangeIndices;
};

export const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

export type SectorColorStyle = {
  bg: string;
  bgEnd: string;
  bgSolid: string;
  bgGradient: string;
  border: string;
  borderColor: string;
  text: string;
  ribbonDropShadow: string;
  ribbonHighlightShadow: string;
};

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function buildRibbonChartEffects(
  bg: string,
  bgEnd: string,
): Pick<SectorColorStyle, "ribbonDropShadow" | "ribbonHighlightShadow"> {
  return {
    ribbonDropShadow: hexToRgba(bgEnd, 0.38),
    ribbonHighlightShadow: hexToRgba(bg, 0.55),
  };
}

function buildSectorGradient(
  hue: number,
  saturation: number,
  lightness: number,
): Pick<
  SectorColorStyle,
  | "bg"
  | "bgEnd"
  | "bgSolid"
  | "bgGradient"
  | "ribbonDropShadow"
  | "ribbonHighlightShadow"
> {
  const bgSolid = hslToHex(hue, saturation, lightness);
  const bg = hslToHex(
    hue,
    Math.min(saturation + 2, 58),
    Math.min(lightness + 6, 86),
  );
  const bgEnd = hslToHex(
    hue,
    Math.min(saturation + 8, 62),
    Math.max(lightness - 10, 58),
  );

  return {
    bgSolid,
    bg,
    bgEnd,
    bgGradient: `linear-gradient(to bottom, ${bg} 0%, ${bgSolid} 55%, ${bgEnd} 100%)`,
    ...buildRibbonChartEffects(bg, bgEnd),
  };
}

function getMomentumBandColor(
  index: number,
  total: number,
): { hue: number; saturation: number; lightness: number } {
  const t = total <= 1 ? 0 : index / (total - 1);

  let hue: number;
  if (t < 0.35) {
    const u = t / 0.35;
    hue = 156 - u * 58;
  } else if (t < 0.65) {
    const u = (t - 0.35) / 0.3;
    hue = 98 - u * 66;
  } else {
    const u = (t - 0.65) / 0.35;
    hue = 32 - u * 24;
  }

  let saturation: number;
  let lightness: number;

  if (t < 0.35) {
    const u = t / 0.35;
    saturation = 56 - u * 6;
    lightness = 72 + u * 8;
  } else if (t < 0.65) {
    const u = (t - 0.35) / 0.3;
    saturation = 50 - u * 6;
    lightness = 80 - u * 4;
  } else {
    const u = (t - 0.65) / 0.35;
    saturation = 44 + u * 8;
    lightness = 76 - u * 10;
  }

  return { hue, saturation, lightness };
}

function buildSectorBorderColor(
  hue: number,
  saturation: number,
  lightness: number,
): string {
  return hslToHex(
    hue,
    Math.min(saturation + 14, 72),
    Math.max(lightness - 22, 44),
  );
}

export function buildSectorsScaleGradient(
  sectors: string[],
  colors: Record<string, SectorColorStyle>,
): string {
  if (sectors.length === 0) return "";
  if (sectors.length === 1) return colors[sectors[0]]?.bgSolid ?? "";

  const stops = sectors.map((sector, index) => {
    const pct = (index / (sectors.length - 1)) * 100;
    return `${colors[sector]?.bgSolid ?? "#cccccc"} ${pct}%`;
  });

  return `linear-gradient(to right, ${stops.join(", ")})`;
}

export function generateSectorColor(
  index: number,
  total: number,
): SectorColorStyle {
  const { hue, saturation, lightness } = getMomentumBandColor(index, total);
  const borderColor = buildSectorBorderColor(hue, saturation, lightness);
  const t = total <= 1 ? 0 : index / (total - 1);
  const border =
    t < 0.35
      ? "border-[#4AAF7A]/55"
      : t < 0.65
        ? "border-[#D49A50]/55"
        : "border-[#D07068]/55";

  return {
    ...buildSectorGradient(hue, saturation, lightness),
    border,
    borderColor,
    text: "text-black",
  };
}

export const formatCurrency = (
  value: number,
  currency = "INR",
  locale = "en-US",
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency as Intl.NumberFormatOptions["currency"],
    minimumFractionDigits: 0, // Ensures no decimals
    maximumFractionDigits: 0,
  }).format(value);
};

interface CurrencyFormatterProps {
  value: number;
}

export const CurrencyFormatter: React.FC<CurrencyFormatterProps> = ({
  value,
}) => {
  return <span>{formatCurrency(value)}</span>;
};

export const NetPercentBadge = ({
  value,
  type,
}: {
  value: number;
  type?: string;
}) => {
  const isPositive = value >= 0;
  const absVal = Math.abs(value);
  return (
    <span
      className={`inline-flex items-center gap-1 font-medium text-sm ${
        isPositive
          ? "text-green-600 dark:text-green-400"
          : "text-red-500 dark:text-red-400"
      }`}
    >
      {isPositive ? (
        <TrendingUp className="w-3.5 h-3.5" />
      ) : (
        <TrendingDown className="w-3.5 h-3.5" />
      )}
      {type && <span className="uppercase font-semibold text-xs">{type} </span>}
      {absVal.toFixed(2)}%
    </span>
  );
};

export const calculateBreakEven = (
  avgBuyPrice: number,
  currentPrice: number,
): number => {
  if (!currentPrice || currentPrice === 0) return 0;
  return ((avgBuyPrice - currentPrice) * 100) / currentPrice;
};

export const whenToChunk = (
  forDate: string | Date,
  pnlPercentSinceBuy: number,
) => {
  if (pnlPercentSinceBuy <= 0) return null;

  const d1 = new Date(forDate);
  const d2 = new Date();
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  let d = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (d === 0) d = 1; // Prevent division by zero

  const taxRate =
    d <= 365
      ? parseFloat(process.env.NEXT_PUBLIC_STCG_TAX_RATE || "0.208")
      : parseFloat(process.env.NEXT_PUBLIC_LTCG_TAX_RATE || "0.125");

  const netReturn = pnlPercentSinceBuy - pnlPercentSinceBuy * taxRate - 0.2;
  const velocity = netReturn * (365 / d);

  let verdict = null;
  if (velocity > 100) verdict = "Best to churn";
  else if (velocity > 70) verdict = "Better to churn";
  else if (velocity > 40) verdict = "Good to churn";

  if (!verdict) return null;

  return {
    verdict,
    taxRate,
    taxType: d <= 365 ? "STCG" : "LTCG",
    days: d,
    netReturnPct: netReturn,
    velocity,
  };
};

export const ChurnVerdictBadge = ({
  forDate,
  pnlPercentSinceBuy,
  pnlSinceBuy,
  className = "",
}: {
  forDate: string | Date;
  pnlPercentSinceBuy: number | null | undefined;
  pnlSinceBuy?: number | null | undefined;
  className?: string;
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipState, setTooltipState] = useState<{
    coords: { top: number; left: number };
    side: "left" | "right" | "top" | "bottom";
  }>({
    coords: { top: 0, left: 0 },
    side: "left",
  });

  useLayoutEffect(() => {
    if (!isOpen) return;

    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    const tr = trigger.getBoundingClientRect();
    const tw = tooltip.offsetWidth || 320;
    const th = tooltip.offsetHeight || 260;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const MARGIN = 12;
    let top = tr.top;
    let left = 0;
    let resolvedSide: "left" | "right" | "top" | "bottom" = "left";

    if (tr.left - tw - MARGIN >= 0) {
      // Enough space on left
      resolvedSide = "left";
      left = tr.left - tw - MARGIN;
      top = tr.top + tr.height / 2 - th / 2;
    } else if (tr.right + tw + MARGIN <= vw) {
      // Enough space on right
      resolvedSide = "right";
      left = tr.right + MARGIN;
      top = tr.top + tr.height / 2 - th / 2;
    } else if (tr.top - th - MARGIN >= 0) {
      // Enough space above
      resolvedSide = "top";
      left = tr.left + tr.width / 2 - tw / 2;
      top = tr.top - th - MARGIN;
    } else {
      // Fallback: below
      resolvedSide = "bottom";
      left = tr.left + tr.width / 2 - tw / 2;
      top = tr.bottom + MARGIN;
    }

    // Clamp so it never goes off-screen
    left = Math.max(8, Math.min(left, vw - tw - 8));
    top = Math.max(8, Math.min(top, vh - th - 8));
    setTooltipState((prev) => {
      if (
        prev.side === resolvedSide &&
        prev.coords.top === top &&
        prev.coords.left === left
      ) {
        return prev;
      }
      return {
        side: resolvedSide,
        coords: { top, left },
      };
    });
  }, [isOpen]);

  if (!pnlPercentSinceBuy || pnlPercentSinceBuy <= 0) return null;
  const result = whenToChunk(forDate, pnlPercentSinceBuy);
  if (!result) return null;

  const { verdict, taxRate, taxType, days: d, netReturnPct } = result;

  const grossGainPct = pnlPercentSinceBuy;
  const grossGainAbs = pnlSinceBuy || 0;
  const taxPct = grossGainPct * taxRate;
  const taxAbs = grossGainAbs * taxRate;
  const netReturnAbs = grossGainAbs - taxAbs;
  const netReturnPercentage = grossGainPct - taxPct;

  const formatAbs = (val: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(val || 0));
  const formatPct = (val: number) => `${Number(val || 0).toFixed(2)}%`;

  const benchmarks = [
    { name: "Nifty Alpha 50", return: 20 },
    { name: "Nifty 500", return: 16 },
    { name: "Nifty Midcap 150", return: 12.5 },
    { name: "Nifty 50", return: 12 },
  ];
  const beatenBenchmark =
    benchmarks.find((b) => netReturnPct > b.return) ||
    benchmarks[benchmarks.length - 1];

  const arrowMap = {
    left: {
      outer:
        "absolute top-1/2 -translate-y-1/2 -right-2 border-y-[8px] border-y-transparent border-l-[8px] border-l-gray-200 dark:border-l-gray-400",
      inner:
        "absolute top-1/2 -translate-y-1/2 -right-[7px] border-y-[8px] border-y-transparent border-l-[8px] border-l-white dark:border-l-gray-900",
    },
    right: {
      outer:
        "absolute top-1/2 -translate-y-1/2 -left-2 border-y-[8px] border-y-transparent border-r-[8px] border-r-gray-200 dark:border-r-gray-400",
      inner:
        "absolute top-1/2 -translate-y-1/2 -left-[7px] border-y-[8px] border-y-transparent border-r-[8px] border-r-white dark:border-r-gray-900",
    },
    top: {
      outer:
        "absolute left-1/2 -translate-x-1/2 -bottom-2 border-x-[8px] border-x-transparent border-t-[8px] border-t-gray-200 dark:border-t-gray-400",
      inner:
        "absolute left-1/2 -translate-x-1/2 -bottom-[7px] border-x-[8px] border-x-transparent border-t-[8px] border-t-white dark:border-t-gray-900",
    },
    bottom: {
      outer:
        "absolute left-1/2 -translate-x-1/2 -top-2 border-x-[8px] border-x-transparent border-b-[8px] border-b-gray-200 dark:border-b-gray-400",
      inner:
        "absolute left-1/2 -translate-x-1/2 -top-[7px] border-x-[8px] border-x-transparent border-b-[8px] border-b-white dark:border-b-gray-900",
    },
  };

  const roundingClass =
    tooltipState.side === "left"
      ? ""
      : tooltipState.side === "right"
        ? ""
        : tooltipState.side === "top"
          ? ""
          : "";

  return (
    <div
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <span
        ref={triggerRef}
        className="text-xs bg-gradient-to-r from-indigo-600 to-cyan-500 dark:from-cyan-400 dark:to-indigo-400 bg-clip-text text-transparent tracking-wide font-medium hover:brightness-125 transition-all duration-300 cursor-pointer"
      >
        {verdict}
      </span>

      {/* Tooltip — always rendered so ref can measure it, visibility toggled via opacity/pointer-events */}
      {isOpen && (
        <div
          ref={tooltipRef}
          style={{
            position: "fixed",
            top: tooltipState.coords.top,
            left: tooltipState.coords.left,
            zIndex: 9999,
          }}
          className={`bg-white dark:bg-gray-900/95 shadow-xl border border-gray-200 dark:border-gray-400 rounded-lg p-3 w-[320px] text-left text-sm text-gray-700 dark:text-gray-300 pointer-events-none ${roundingClass}`}
        >
          <div className={`w-0 h-0 ${arrowMap[tooltipState.side].outer}`} />
          <div className={`w-0 h-0 ${arrowMap[tooltipState.side].inner}`} />

          <h4 className="font-semibold text-gray-900 dark:text-white mb-2 pb-2 border-b border-gray-100 dark:border-gray-700">
            Why is it {verdict}?
          </h4>

          <div className="space-y-1.5 mb-3">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Current Profit:
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {formatAbs(grossGainAbs)} | {formatPct(grossGainPct)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600 dark:text-gray-400">
                {taxType} + cess ({Number(taxRate * 100).toFixed(1)}%):
              </span>
              <span className="font-semibold text-red-500 dark:text-red-400">
                - {formatAbs(taxAbs)} | {formatPct(taxPct)}
              </span>
            </div>
            <div className="flex justify-between pt-1">
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Net Profit:
              </span>
              <span className="font-bold text-green-600 dark:text-green-400">
                {formatAbs(netReturnAbs)} | {formatPct(netReturnPercentage)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Duration:
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {d} {d === 1 ? "day" : "days"}
              </span>
            </div>
          </div>

          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 leading-relaxed text-wrap border-t border-gray-100 dark:border-gray-800">
            Your net profit after {taxType} is beating 1-year benchmark return
            of <strong>{beatenBenchmark.name}</strong>{" "}
            {d < 30 && "compared to daily pro data basis"}. Hence it is{" "}
            <strong>{verdict}</strong> and reallocate in next opportunity.
          </p>

          <div className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded border border-gray-100 dark:border-gray-700">
            <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              1-Year Benchmarks:
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              {benchmarks.map((b) => (
                <div
                  key={b.name}
                  className={`flex justify-between ${
                    beatenBenchmark.name === b.name
                      ? "text-indigo-600 dark:text-indigo-400 font-bold"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  <span>{b.name}:</span>
                  <span>{b.return}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const formatVal = (val: number) =>
  Number(val || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
